import { useEffect, useMemo, useRef } from 'react'
import { useEditor } from '@tiptap/react'
import { buildComposerExtensions } from './buildComposerExtensions'
import { buildComposerEditorProps } from './buildComposerEditorProps'
import { useComposerMentionRefs } from './useComposerMentionRefs'

export function useComposerEditorInstance({ placeholder, readOnly, onEnterSubmit, editorAriaLabel, enableMentions }) {
  const submitHandlerRef = useRef(onEnterSubmit)
  submitHandlerRef.current = onEnterSubmit
  const mentionRefs = useComposerMentionRefs()

  const extensions = useMemo(
    () =>
      buildComposerExtensions({
        placeholder,
        enableMentions,
        getOnEnter: () => (mentionRefs.mentionOpenRef.current ? undefined : submitHandlerRef.current),
      }),
    [placeholder, enableMentions, mentionRefs.mentionOpenRef],
  )

  const editor = useEditor({
    extensions,
    content: { type: 'doc', content: [{ type: 'paragraph' }] },
    editable: !readOnly,
    editorProps: buildComposerEditorProps({
      editorAriaLabel,
      mentionOpenRef: mentionRefs.mentionOpenRef,
      mentionFlatCountRef: mentionRefs.mentionFlatCountRef,
      setMentionActiveIndex: mentionRefs.setMentionActiveIndex,
      setMentionTrigger: mentionRefs.setMentionTrigger,
      setMentionAnchor: mentionRefs.setMentionAnchor,
    }),
  })

  // TipTap keeps the extension instance after mount, so update its option and
  // force a decoration pass when a selected excerpt changes the prompt.
  useEffect(() => {
    if (!editor || editor.isDestroyed) return
    const placeholderExtension = editor.extensionManager.extensions.find((extension) => extension.name === 'placeholder')
    if (!placeholderExtension || placeholderExtension.options.placeholder === placeholder) return
    placeholderExtension.options.placeholder = placeholder
    editor.view.dispatch(editor.state.tr)
  }, [editor, placeholder])

  return { editor, mentionRefs }
}
